import { Play, Pause, Clock, CheckCircle, XCircle, Trash, PencilSimple, Lightning, ClockCounterClockwise } from '@phosphor-icons/react';

const STATUS_COLORS = {
  true: { bg: 'bg-[#dcfce7]', text: 'text-[#16a34a]', label: 'Last run OK' },
  false: { bg: 'bg-[#fee2e2]', text: 'text-[#dc2626]', label: 'Last run failed' },
  null: { bg: 'bg-[#f2f4f6]', text: 'text-[#596065]', label: 'Never run' },
};

export const AgentCard = ({ agent, onRun, onToggle, onEdit, onDelete, onViewHistory }) => {
  const status = STATUS_COLORS[agent.last_run_success] || STATUS_COLORS[null];
  const trigger = agent.trigger || {};
  const triggerText = formatTrigger(trigger);

  return (
    <div className="bg-white rounded-xl border border-[#f0f1f3] hover:border-[#d0e4f7] transition-colors overflow-hidden" data-testid={`agent-card-${agent.agent_id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${agent.enabled ? 'bg-[#0f172a]' : 'bg-[#e2e8f0]'}`}>
              <Lightning size={18} className={agent.enabled ? 'text-[#f59e0b]' : 'text-[#94a3b8]'} weight="fill" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-[#2d3338]">{agent.name}</h3>
              <p className="text-[10px] text-[#acb3b8]">{agent.actions?.length || 0} steps</p>
            </div>
          </div>
          <button
            onClick={() => onToggle(agent.agent_id, !agent.enabled)}
            className={`relative w-10 h-5 rounded-full transition-colors ${agent.enabled ? 'bg-[#16a34a]' : 'bg-[#d1d5db]'}`}
            data-testid={`toggle-agent-${agent.agent_id}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${agent.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {agent.description && (
          <p className="text-xs text-[#596065] mb-2 line-clamp-2">{agent.description}</p>
        )}

        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1 text-[10px] text-[#596065]">
            <Clock size={11} />
            <span>{triggerText}</span>
          </div>
          <div className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md ${status.bg} ${status.text}`}>
            {agent.last_run_success === true && <CheckCircle size={11} weight="fill" />}
            {agent.last_run_success === false && <XCircle size={11} weight="fill" />}
            <span>{status.label}</span>
          </div>
        </div>

        {/* Action steps preview */}
        <div className="flex flex-wrap gap-1 mb-3">
          {(agent.actions || []).map((a, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#f2f4f6] text-[#596065]">
              {a.name || a.type}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-[#f2f4f6]">
          <button
            onClick={() => onRun(agent.agent_id)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] transition-colors"
            data-testid={`run-agent-${agent.agent_id}`}
          >
            <Play size={11} weight="fill" /> Run Now
          </button>
          <button
            onClick={() => onEdit(agent)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border border-[#e2e8f0] text-[#596065] hover:bg-[#f2f4f6] transition-colors"
            data-testid={`edit-agent-${agent.agent_id}`}
          >
            <PencilSimple size={11} /> Edit
          </button>
          {onViewHistory && (
            <button
              onClick={() => onViewHistory(agent)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border border-[#e2e8f0] text-[#596065] hover:bg-[#f2f4f6] transition-colors"
              data-testid={`history-agent-${agent.agent_id}`}
            >
              <ClockCounterClockwise size={11} /> History
            </button>
          )}
          <button
            onClick={() => onDelete(agent.agent_id)}
            className="ml-auto p-1.5 rounded-lg text-[#acb3b8] hover:text-[#dc2626] hover:bg-[#fee2e2] transition-colors"
            data-testid={`delete-agent-${agent.agent_id}`}
          >
            <Trash size={13} />
          </button>
        </div>
      </div>

      {/* Last run info */}
      {agent.last_run && (
        <div className="px-4 py-2 bg-[#fafbfc] border-t border-[#f0f1f3] text-[10px] text-[#acb3b8]">
          Last run: {formatRelativeTime(agent.last_run)}
        </div>
      )}
    </div>
  );
};

function formatTrigger(trigger) {
  if (!trigger || !trigger.type) return 'Manual only';
  const h = trigger.hour ?? 9;
  const m = trigger.minute ?? 0;
  const time = `${h > 12 ? h - 12 : h === 0 ? 12 : h}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  if (trigger.day_of_month) return `${time} on ${trigger.day_of_month}${trigger.day_of_month === 1 ? 'st' : trigger.day_of_month === 2 ? 'nd' : trigger.day_of_month === 3 ? 'rd' : 'th'} monthly`;
  if (trigger.week_interval === 2) {
    const dayLabel = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }[trigger.days] || trigger.days;
    return `${time} fortnightly (${dayLabel})`;
  }
  const days = trigger.days || '';
  if (days === 'mon,tue,wed,thu,fri') return `${time} weekdays`;
  if (days === 'mon,tue,wed,thu,fri,sat,sun') return `${time} daily`;
  if (days && !days.includes(',')) {
    const dayLabel = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }[days] || days;
    return `${time} weekly (${dayLabel})`;
  }
  return `${time} ${days}`;
}

function formatRelativeTime(ts) {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
